import { DEVICE_CHARACTERISTICS_STATEMENTS } from "@config";
import {
  AssistantProcess,
  Device,
  Document,
  DocumentAnswer,
  DocumentAnswerType,
  DocumentVersion,
  SSEMessage,
  TEMPLATE_ID,
} from "@models";
import {
  DataKeyWithDefinedValue,
  DocumentDataKey,
  RegulatoryTypeEnum,
  StepValue,
  TemplateConfig,
  TemplateElement,
} from "@types";
import {
  getAnswerType,
  getDocConfigByElementId,
  getElementConfigByDataKey,
  getLatestDocumentVersion,
  hasHardware,
  hasSoftware,
  isSoftwareOnly,
} from "@utils";
import { SSEStream } from "./sse";

export const gatherContext = ({
  elementId,
  documents,
  documentVersion,
  process,
  templatesConfig,
}: {
  elementId: DocumentDataKey;
  documents: Document[];
  documentVersion?: DocumentVersion;
  process?: AssistantProcess;
  templatesConfig: TemplateConfig;
}): DataKeyWithDefinedValue[] => {
  const context: DataKeyWithDefinedValue[] = [];
  const docConfig = getDocConfigByElementId(elementId, templatesConfig);
  const isRCDElement = docConfig.regulatoryType === RegulatoryTypeEnum.RCD;

  const contextFromDataKey = getContextForDataKey(
    elementId,
    documents,
    templatesConfig,
    documentVersion
  );

  context.push(...contextFromDataKey);

  if (process) {
    const currentAnswer = process.state.changes.find(
      (c) => c.dataKeyId === elementId
    );

    if (currentAnswer?.previousAnswer && !isRCDElement) {
      const value = getAnswerFormattedForContext(
        elementId,
        currentAnswer.previousAnswer,
        templatesConfig
      );

      context.push({
        id: elementId,
        value,
      });
    }

    // include all elements from relevant documents (BUG_FIX_REPORT, CHANGE_REQUEST)
    const relevantElements = templatesConfig[
      TEMPLATE_ID.BUG_FIX_REPORT
    ].elements.concat(templatesConfig[TEMPLATE_ID.CHANGE_REQUEST].elements);

    process.state.changes.forEach((change) => {
      const relevantElement = relevantElements.find(
        (e) => e.id === change.dataKeyId
      );

      // Exclude the current element from the context
      if (relevantElement && change.dataKeyId !== elementId) {
        let answer;

        if (change.acceptedAnswer) {
          answer = change.acceptedAnswer;
        } else if (change.previousAnswer) {
          answer = change.previousAnswer;
        }

        if (!answer) {
          return;
        }

        context.push({
          id: relevantElement.id,
          value: getAnswerFormattedForContext(
            relevantElement.id,
            answer,
            templatesConfig
          ),
        });
      }
    });
  }

  return context;
};

export function getMissingContextTemplatesIds({
  elementId,
  documents,
  documentVersion,
  templatesConfig,
}: {
  elementId: DocumentDataKey;
  documents: Document[];
  documentVersion?: DocumentVersion;
  templatesConfig: TemplateConfig;
}): {
  missingTemplatesIds: string[];
} {
  const missingTemplatesIds: string[] = [];

  const docConfig = getDocConfigByElementId(elementId, templatesConfig);

  // Get element-specific context
  const element = docConfig.elements.find((e) => e.id === elementId);
  if (!element) {
    throw new Error(`Element ${elementId} not found in template config`);
  }

  const contextFromDataKey = getContextForDataKey(
    elementId,
    documents,
    templatesConfig,
    documentVersion
  );

  // Track missing context only for this specific element
  if (element.context) {
    element.context.forEach((contextKey) => {
      if (!contextFromDataKey.find((c) => c.id === contextKey)) {
        const templateKey = getDocConfigByElementId(
          contextKey,
          templatesConfig
        ).templateKey;
        missingTemplatesIds.push(templateKey);
      }
    });
  }

  return { missingTemplatesIds };
}

export function getChangeInformation(process: AssistantProcess): string {
  return process.state.description + "\n" + process.state.checkboxes.join("\n");
}

export function generateSuggestion({
  elementId,
  device,
  documents,
  documentVersion,
  mock,
  process,
  templatesConfig,
}: {
  elementId: DocumentDataKey;
  device: Device;
  documents: Document[];
  documentVersion?: DocumentVersion;
  mock?: boolean;
  process?: AssistantProcess;
  templatesConfig: TemplateConfig;
}): AsyncGenerator<SSEMessage> {
  const element = getElementConfigByDataKey(elementId, templatesConfig);
  const docConfig = getDocConfigByElementId(elementId, templatesConfig);

  const isRCDElement = docConfig.regulatoryType === RegulatoryTypeEnum.RCD;

  // We have a change suggestion if the process is present and there is a previous answer
  const isChangeSuggestion =
    process &&
    // element.prompt &&
    !isRCDElement &&
    process.state.changes.find(
      (c) => c.dataKeyId === elementId && c.previousAnswer
    );

  if (mock) {
    const elementType = element.element.type;
    return mockSuggestionGenerator(elementType);
  }
  if (!documentVersion && docConfig.regulatoryType !== RegulatoryTypeEnum.RCD) {
    throw new Error("A documentVersion is required for non RCD documents");
  }

  const context = gatherContext({
    elementId,
    documents,
    documentVersion,
    process,
    templatesConfig,
  });

  const endpoint = isChangeSuggestion
    ? "/change-suggestion"
    : "/template-suggestion/v2";

  const { element: elementConfig } = element;

  return SSEStream(endpoint, {
    characteristics: deviceCharacteristics(device),
    device_name: device.name,
    device_description: device.description,
    additional_context: [
      ...context,
      ...(process
        ? [
            {
              id: "change_information" as DocumentDataKey,
              value: getChangeInformation(process),
            },
          ]
        : []),
    ],
    components: device.components,
    context_files: [],
    step: {
      question: elementConfig.options.label,
      description: elementConfig.options.helperText || "",
      default_value: elementConfig.options.default || "",
      response_type: elementConfig.type,
      output_config: {
        ...(elementConfig.type === "table" && {
          columns: elementConfig.options.columns.map((c) => c.name),
        }),
        ...(elementConfig.type === "select" && {
          options: elementConfig.options.options,
        }),
      },
      id: element.id,
    },
  });
}
/**
 * For a given element returns the other ids of the that are used as context. Also gives status of whether the context has values or not. Map
 * @param element TemplateElement
 */

export type ContextState = Map<
  DocumentDataKey,
  { complete: boolean; value: string | undefined }
>;

export function getContextConfig(
  element: TemplateElement,
  documents: Document[]
): ContextState {
  const contextConfig = new Map<
    DocumentDataKey,
    { complete: boolean; value: string | undefined }
  >();
  element.context?.forEach((contextKey) => {
    const contextElementAnswers = documents
      .flatMap((d) => {
        const latestVersion = getLatestDocumentVersion(d);
        return latestVersion?.answers || [];
      })
      .find((a) => a.element === contextKey);
    contextConfig.set(contextKey, {
      complete: contextElementAnswers?.answer !== undefined,
      value: contextElementAnswers?.answer,
    });
  });

  const sortedEntriesFirstIncomplete = Array.from(contextConfig.entries()).sort(
    (a, b) => {
      if (a[1].complete !== b[1].complete) {
        return a[1].complete ? 1 : -1;
      }
      return 0;
    }
  );

  return new Map(sortedEntriesFirstIncomplete);
}

function deviceCharacteristics(device: Device): string[] {
  const statements = [];
  if (hasHardware(device))
    statements.push(DEVICE_CHARACTERISTICS_STATEMENTS.hasHardware);
  if (hasSoftware(device) && !isSoftwareOnly(device))
    statements.push(DEVICE_CHARACTERISTICS_STATEMENTS.hasSoftware);
  if (isSoftwareOnly(device))
    statements.push(DEVICE_CHARACTERISTICS_STATEMENTS.isSoftwareOnly);
  return statements;
}

export function getContextForDataKey(
  dataKey: DocumentDataKey,
  documents: Document[],
  templatesConfig: TemplateConfig,
  documentVersion?: DocumentVersion
): DataKeyWithDefinedValue[] {
  const element = getElementConfigByDataKey(dataKey, templatesConfig);
  const context: DataKeyWithDefinedValue[] = [];

  // Get all answers from the documentVersion and all other documents
  let answers: DocumentAnswer[] = [];

  const document = documents.find((d) => d.id === documentVersion?.documentId);

  answers.push(
    ...documents
      .filter((d) => d.versions.length > 0)
      // IMPORTANT filter out documents with the same name but different ids than that of the current document
      .filter((d) =>
        d.templateKey === document?.templateKey
          ? d.id === documentVersion?.id
          : true
      )
      .map((d) => d.versions[0])
      // Filter out the current document and documents without versions
      .filter((v) => v.id !== documentVersion?.id)
      .map((v) => v.answers)
      .flat()
  );

  if (documentVersion) {
    answers.push(...documentVersion.answers);
  }

  // If the context specifies elements that are not in the current document we search for them in the other documents
  answers.forEach((a) => {
    if (element.context?.includes(a.element)) {
      context.push({
        id: a.element,
        value: getAnswerFormattedForContext(a.element, a, templatesConfig),
      });
    }
  });

  if (element.prePromptTransformerConfig) {
    const adjustedDataKeys = runPrePromptTransformer(element, answers);
    adjustedDataKeys.forEach(({ id, value }) => {
      context.push({
        id,
        value,
      });
    });
  }

  return context;
}

export const getAnswerFormattedForContext = (
  elementId: DocumentDataKey,
  answer: StepValue,
  templatesConfig: TemplateConfig
): string => {
  const elementConfig = getElementConfigByDataKey(elementId, templatesConfig);
  const { element } = elementConfig;

  const answerType = getAnswerType(elementId, templatesConfig);
  let formattedAnswer = "";
  if (answerType === DocumentAnswerType.LIST) {
    // Answer list items always need a transformer since they are outputted as a table
    if (!("outputTransformer" in element.options)) {
      throw new Error(`Output transformer not found for element ${elementId}`);
    }

    if (element.type !== "answerItemsElement") {
      throw new Error(`Element ${elementId} is not an answerItemsElement`);
    }

    if (!Array.isArray(answer.answerItems) && elementConfig.required) {
      throw new Error(`Answer items are not an array for element ${elementId}`);
    }
    formattedAnswer = element.options.outputTransformer({
      answer: answer.answerItems || [],
      element: element,
    });
  } else if (answerType === DocumentAnswerType.FILE) {
    formattedAnswer = answer.answerFileId || "";
  } else {
    formattedAnswer = answer.answer || "";
  }

  return formattedAnswer;
};

export const runPrePromptTransformer = (
  element: TemplateElement,
  answers: DocumentAnswer[]
): DataKeyWithDefinedValue[] => {
  if (answers.length === 0) {
    return [];
  }

  if (!element.prePromptTransformerConfig) {
    throw new Error(
      `Pre prompt transformer config not found for element ${element.id}`
    );
  }
  // find the answer for the required input in all the document answers
  const inputs = answers
    .filter((a) =>
      element.prePromptTransformerConfig?.inputs.includes(a.element)
    )
    .map((a) => [a.element, a.answer]);

  //run the transformer
  const adjustedDataKeys =
    inputs.length > 0
      ? element.prePromptTransformerConfig.transformer(
          Object.fromEntries(inputs)
        )
      : [];

  return adjustedDataKeys;
};

export async function* mockSuggestionGenerator(
  mock: TemplateElement["element"]["type"] = "textField"
): AsyncGenerator<SSEMessage> {
  try {
    if (mock === "textField") {
      // First 3 iterations with "mock"
      for (let i = 1; i <= 5; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        yield {
          event: "message",
          id: "mock",
          data: {
            content: i.toString() + "\n",
            metadata: { langgraph_node: "__start__" },
          },
        };
      }

      // Next 4 iterations with "Reviewing changes"
      for (let i = 6; i <= 10; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        yield {
          event: "message",
          id: "mock",
          data: {
            content: i.toString() + "\n",
            metadata: { langgraph_node: "Reviewing changes" },
          },
        };
      }

      // Next 4 iterations with "Applying suggestion"
      for (let i = 11; i <= 15; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        yield {
          event: "message",
          id: "mock",
          data: {
            content: i.toString() + "\n",
            metadata: { langgraph_node: "Applying suggestion" },
          },
        };
      }

      for (let i = 16; i <= 20; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        yield {
          event: "message",
          id: "mock",
          data: {
            content: i.toString() + "\n",
            metadata: { langgraph_node: "apply_changes" },
          },
        };
      }
    } else if (mock === "table") {
      // for (let i = 1; i <= 5; i++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const tableReturn =
        "| Employee Name        | Start Date | Functional Group | Manager / Supervisor |\n| -------------------- | ---------- | ----------------- | --------------------- |\n|  John Doe             | 2024-01-01 | Quality           | Jane Doe              |\n|  Mary Smith           | 2024-02-15 | Engineering       | Bob Wilson            |\n|  Mary Smith  2          | 2024-02-15 | Engineering       | Bob Wilson            |";

      const tableLines = tableReturn.split("\n");
      for (const line of tableLines) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        yield {
          event: "message",
          id: "mock",
          data: {
            content: line + "\n",
            metadata: { langgraph_node: "apply_changes" },
          },
        };
      }
    } else if (mock === "parsedFileUploadElement") {
      // for (let i = 1; i <= 5; i++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const parsedFileUploadElementReturn = `("software users" OR "application users" OR "digital health users" OR "health app users" OR "medical software users") AND ("software application" OR "digital health tool" OR "health management software" OR "medical app" OR "health tracking software") AND ("software usability" OR "software efficiency" OR "software effectiveness" OR "software reliability" OR "software safety") AND ("user satisfaction" OR "software performance" OR "software functionality" OR "software reliability" OR "software safety") AND ("humans"[MeSH Terms]) AND ("english"[Language]) AND ("2015/01/31"[PDAT] : "2025/01/31"[PDAT])`;
      yield {
        event: "message",
        id: "mock",
        data: {
          content: parsedFileUploadElementReturn + "\n",
          metadata: { langgraph_node: "apply_changes" },
        },
      };
      // }
    }
  } catch (error) {
    yield {
      event: "error",
      id: "mock",
      data: {
        content: JSON.stringify(error),
        metadata: {},
      },
    };
  }
}
